import React, {memo, useCallback, useMemo, useState} from "react"
import {useRPC} from "../../../../hooks/rpc"
import {SupervisorConfigModel} from "../../../../types/supervisor-config"
import {useDispatch, useSelector} from "react-redux"
import {useLocalization} from "../../../../hooks/localization"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {useKeyboard} from "../../../../hooks/keyboard"
import {useMessage} from "../../../../hooks/message"
import {useHistory} from "react-router-dom"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import {setSupervisorConfigListAction} from "../../../../store/control/actions"
import "./index.scss"

const BACK_URL = "/settings/advanced-settings"
export const SupervisorConfigScreen = memo(function SupervisorConfigScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const {failure, success} = useMessage()
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard()
    const history = useHistory()
    const dispatch = useDispatch()
    const supervisorConfigList = useSelector(state => state.control.supConfigList)
    const [listenProtocol, setListenProtocol] = useState<string>(supervisorConfigList?.listen_protocol)
    const [listenAddress, setListenAddress] = useState<string>(supervisorConfigList?.listen_address)
    const [executorType, setExecutorType] = useState<string>(supervisorConfigList?.executor_type)
    const [dbFolder, setDbFolder] = useState<string>(supervisorConfigList?.db_folder)
    const [internalDbFolder, setInternalDbFolder] = useState<string>(supervisorConfigList?.internal_db_folder)
    const [logFolder, setLogFolder] = useState<string>(supervisorConfigList?.log_folder)
    const [authToken, setAuthToken] = useState<string>(supervisorConfigList?.notification?.auth_token)
    const [url, setUrl] = useState<string>(supervisorConfigList?.notification?.url)
    const [alarmPeriodInSeconds, setAlarmPeriodInSeconds] = useState<string>(
        String(supervisorConfigList?.notification?.alarm_period_in_seconds)
    )
    const [armDisarmPeriodInSeconds, setArmDisarmPeriodInSeconds] = useState<string>(
        String(supervisorConfigList?.notification?.arm_disarm_period_in_seconds)
    )
    const [bellPeriodInSeconds, setBellPeriodInSeconds] = useState<string>(
        String(supervisorConfigList?.notification?.bell_period_in_seconds)
    )
    const [intercomPeriodInSeconds, setIntercomPeriodInSeconds] = useState<string>(
        String(supervisorConfigList?.notification?.intercom_period_in_seconds)
    )
    const [intertextPeriodInSeconds, setIntertextPeriodInSeconds] = useState<string>(
        String(supervisorConfigList?.notification?.intertext_period_in_seconds)
    )
    const [defaultScenarioDelayMilliseconds, setDefaultScenarioDelayMilliseconds] = useState<string>(
        String(supervisorConfigList?.default_scenario_delay_milliseconds)
    )
    const [updateWholeAndFraction, setUpdateWholeAndFraction] = useState<boolean>(
        supervisorConfigList?.update_whole_and_fraction_after_ack_for_glass_devices
    )
    const [isSendLocalReturns, setIsSendLocalReturns] = useState<boolean>(
        supervisorConfigList?.is_send_local_returns_ack
    )
    const [timeOffsetInMinutes, setTimeOffsetInMinutes] = useState<string>(
        String(supervisorConfigList?.time_offset_in_minutes)
    )
    const [isDocker, setIsDocker] = useState<boolean>(supervisorConfigList?.is_docker)
    const [heartBeatPeriod, setHeartBeatPeriod] = useState<string>(
        String(supervisorConfigList?.i_heart_beat?.heart_beat_period_in_seconds)
    )
    const [timezone, setTimezone] = useState<string>(supervisorConfigList?.timezone)
    const [isManualTimezone, setIsManualTimezone] = useState<boolean>(supervisorConfigList?.is_manual_time_offset)
    const [heartBeatUrl, setHeartBeatUrl] = useState<string>(supervisorConfigList?.i_heart_beat?.heart_beat_url)
    const [heartBeatAuth, setHeartBeatAuth] = useState<string>(supervisorConfigList?.i_heart_beat?.heart_beat_auth)
    const [callAlarmUrl, setCallAlarmUrl] = useState<string>(supervisorConfigList?.call_alarm?.url)
    const [callAlarmToken, setCallAlarmToken] = useState<string>(supervisorConfigList?.call_alarm?.token)
    const [callAlarmCookie, setCallAlarmCookie] = useState<string>(supervisorConfigList?.call_alarm?.cookie)
    const [dayRate, setDayRate] = useState<string>(supervisorConfigList?.energy_monitor?.day_rate)
    const [nightRate, setNightRate] = useState<string>(supervisorConfigList?.energy_monitor?.night_rate)
    const [energyFee, setEnergyFee] = useState<string>(supervisorConfigList?.energy_monitor?.energy_fee)
    const [invoiceDate, setInvoiceDate] = useState<string>(supervisorConfigList?.energy_monitor?.invoice_date)
    const [currencyUnit, setCurrencyUnit] = useState<string>(supervisorConfigList?.energy_monitor?.currency_unit)
    const [devicePulseKwh, setDevicePulseKwh] = useState<string>(supervisorConfigList?.energy_monitor?.device_pulse_kwh)
    const [realTimeCheckSeconds, setRealTimeCheckSeconds] = useState<string>(
        String(supervisorConfigList?.energy_monitor?.real_time_check_seconds)
    )
    const [maxPoint, setMaxPoint] = useState<string>(supervisorConfigList?.energy_monitor?.max_point)
    const [version, setVersion] = useState<string>(supervisorConfigList?.version)
    const [conciergeUrlPort, setConciergeUrlPort] = useState<string>(
        supervisorConfigList?.concierge_settings?.concierge_url_port
    )
    const [conciergeSchemeForMessage, setConciergeSchemeForMessage] = useState<string>(
        supervisorConfigList?.concierge_settings?.concierge_scheme_for_message
    )
    const [conciergeScheme, setConciergeScheme] = useState<string>(
        supervisorConfigList?.concierge_settings?.concierge_scheme
    )
    const [conciergeApiWebsocket, setConciergeApiWebsocket] = useState<string>(
        supervisorConfigList?.concierge_settings?.concierge_api_websocket
    )
    const [conciergeApiMessage, setConciergeApiMessage] = useState<string>(
        supervisorConfigList?.concierge_settings?.concierge_api_message
    )
    const [weatherApiKey, setWeatherApiKey] = useState<string>(supervisorConfigList?.weather_api_key)
    const [weatherApiUrl, setWeatherApiUrl] = useState<string>(supervisorConfigList?.weather_api_url)
    const [weatherApiValue, setWeatherApiValue] = useState<string>(supervisorConfigList?.weather_api_value)
    const [isActiveLogger, setIsActiveLogger] = useState<boolean>(supervisorConfigList?.logger_server?.enabled)
    const [loggerUrl, setLoggerUrl] = useState<string>(String(supervisorConfigList?.logger_server?.url))
    const [selectedLanguage, setSelectedLanguage] = useState<string>(supervisorConfigList?.selected_lang)
    const [rfTestWorkedTime, setRfTestWorkedTime] = useState<string>(String(supervisorConfigList?.rf_test?.WorkedTime))
    const [rfTestDebugMode, setRfTestDebugMode] = useState<boolean>(supervisorConfigList?.rf_test?.debug_mode)
    const [rfTestIsEnabled, setRfTestIsEnabled] = useState<boolean>(supervisorConfigList?.rf_test?.is_enabled)

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL)
    }, [history])

    const onSavePressed = useCallback(() => {
        const supervisorConfigModel: SupervisorConfigModel = {
            call_alarm: {
                cookie: callAlarmCookie,
                token: callAlarmToken,
                url: callAlarmUrl
            },
            concierge_settings: {
                concierge_url_port: conciergeUrlPort,
                concierge_scheme_for_message: conciergeSchemeForMessage,
                concierge_scheme: conciergeScheme,
                concierge_api_websocket: conciergeApiWebsocket,
                concierge_api_message: conciergeApiMessage
            },
            db_folder: dbFolder,
            default_scenario_delay_milliseconds: Number(defaultScenarioDelayMilliseconds),
            energy_monitor: {
                day_rate: dayRate,
                night_rate: nightRate,
                energy_fee: energyFee,
                invoice_date: invoiceDate,
                currency_unit: currencyUnit,
                device_pulse_kwh: devicePulseKwh,
                real_time_check_seconds: Number(realTimeCheckSeconds),
                max_point: maxPoint
            },
            executor_type: executorType,
            i_heart_beat: {
                heart_beat_period_in_seconds: Number(heartBeatPeriod),
                heart_beat_url: heartBeatUrl,
                heart_beat_auth: heartBeatAuth
            },
            internal_db_folder: internalDbFolder,
            is_docker: isDocker,
            is_send_local_returns_ack: isSendLocalReturns,
            listen_protocol: listenProtocol,
            listen_address: listenAddress,
            log_folder: logFolder,
            notification: {
                auth_token: authToken,
                url: url,
                alarm_period_in_seconds: Number(alarmPeriodInSeconds),
                arm_disarm_period_in_seconds: Number(armDisarmPeriodInSeconds),
                bell_period_in_seconds: Number(bellPeriodInSeconds),
                intercom_period_in_seconds: Number(intercomPeriodInSeconds),
                intertext_period_in_seconds: Number(intertextPeriodInSeconds)
            },
            update_whole_and_fraction_after_ack_for_glass_devices: updateWholeAndFraction,
            time_offset_in_minutes: Number(timeOffsetInMinutes),
            timezone: timezone,
            is_manual_time_offset: isManualTimezone,
            version: version,
            weather_api_key: weatherApiKey,
            weather_api_url: weatherApiUrl,
            weather_api_value: weatherApiValue,
            logger_server: {
                enabled: isActiveLogger,
                url: loggerUrl
            },
            selected_lang: selectedLanguage,
            rf_test: {
                WorkedTime: Number(rfTestWorkedTime),
                debug_mode: rfTestDebugMode,
                is_enabled: rfTestIsEnabled
            }
        }
        return rpc
            .updateSupervisorConfig(supervisorConfigModel)
            .then(() => {
                dispatch(setSupervisorConfigListAction(supervisorConfigModel, "supConfig"))
                success(t("successfully_edited"))
                onBackPressed()
            })
            .catch(failure)
    }, [
        onBackPressed,
        failure,
        success,
        t,
        dispatch,
        rpc,
        listenProtocol,
        listenAddress,
        executorType,
        dbFolder,
        internalDbFolder,
        logFolder,
        authToken,
        url,
        alarmPeriodInSeconds,
        armDisarmPeriodInSeconds,
        bellPeriodInSeconds,
        intercomPeriodInSeconds,
        intertextPeriodInSeconds,
        defaultScenarioDelayMilliseconds,
        updateWholeAndFraction,
        isSendLocalReturns,
        timeOffsetInMinutes,
        isDocker,
        heartBeatPeriod,
        heartBeatUrl,
        heartBeatAuth,
        callAlarmUrl,
        callAlarmToken,
        callAlarmCookie,
        dayRate,
        nightRate,
        energyFee,
        invoiceDate,
        currencyUnit,
        devicePulseKwh,
        realTimeCheckSeconds,
        maxPoint,
        version,
        conciergeUrlPort,
        conciergeSchemeForMessage,
        conciergeScheme,
        conciergeApiWebsocket,
        conciergeApiMessage,
        weatherApiKey,
        timezone,
        isManualTimezone,
        weatherApiUrl,
        weatherApiValue,
        isActiveLogger,
        loggerUrl,
        selectedLanguage,
        rfTestWorkedTime,
        rfTestDebugMode,
        rfTestIsEnabled
    ])

    const buttons = useMemo(
        () => [
            {
                text: t("save"),
                className: "green",
                onClick: onSavePressed
            }
        ],
        [t, onSavePressed]
    )

    const onBlur = useCallback(() => {
        setActiveInput(null)
    }, [setActiveInput])
    return (
        <PasswordContainer requiredRole="developer" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={BACK_URL}
                currentInput={currentInput}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={""}
                            label={"Selected language"}
                            value={selectedLanguage}
                            setValue={setSelectedLanguage}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Listen protocol"}
                            value={listenProtocol}
                            setValue={setListenProtocol}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Listen address"}
                            value={listenAddress}
                            setValue={setListenAddress}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Executor type"}
                            value={executorType}
                            setValue={setExecutorType}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Database folder"}
                            value={dbFolder}
                            setValue={setDbFolder}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Internal database folder"}
                            value={internalDbFolder}
                            setValue={setInternalDbFolder}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Log folder"}
                            value={logFolder}
                            setValue={setLogFolder}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <div className="supervisor-config-set-row inside-margin">
                            <div className="supervisor-config-title">Notification</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Auth token"}
                                value={authToken}
                                setValue={setAuthToken}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Url"}
                                value={url}
                                setValue={setUrl}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Alarm period in seconds"}
                                value={alarmPeriodInSeconds}
                                setValue={setAlarmPeriodInSeconds}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Arm disarm period in seconds"}
                                value={armDisarmPeriodInSeconds}
                                setValue={setArmDisarmPeriodInSeconds}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Bell period in seconds"}
                                value={bellPeriodInSeconds}
                                setValue={setBellPeriodInSeconds}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Intercom period in seconds"}
                                value={intercomPeriodInSeconds}
                                setValue={setIntercomPeriodInSeconds}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Intertext period in seconds"}
                                value={intertextPeriodInSeconds}
                                setValue={setIntertextPeriodInSeconds}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="supervisor-config-set-row">
                            <div className="supervisor-config-title">Energy Monitor</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Day rate"}
                                value={dayRate}
                                setValue={setDayRate}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Night rate"}
                                value={nightRate}
                                setValue={setNightRate}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Energy fee"}
                                value={energyFee}
                                setValue={setEnergyFee}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Invoice date"}
                                value={invoiceDate}
                                setValue={setInvoiceDate}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Currency unit"}
                                value={currencyUnit}
                                setValue={setCurrencyUnit}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Device pulse kwh"}
                                value={devicePulseKwh}
                                setValue={setDevicePulseKwh}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Real time check seconds"}
                                value={realTimeCheckSeconds}
                                setValue={setRealTimeCheckSeconds}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Max point"}
                                value={maxPoint}
                                setValue={setMaxPoint}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="supervisor-config-set-row">
                            <div className="supervisor-config-title">Logger Server</div>
                            <SettingCheckBoxComponent
                                label={"Is active?"}
                                set={() => setIsActiveLogger(p => !p)}
                                value={isActiveLogger}
                            />
                            {renderIf(isActiveLogger)(() => (
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"URL"}
                                    value={loggerUrl}
                                    setValue={setLoggerUrl}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                            ))}
                        </div>
                        <div className="supervisor-config-set-row">
                            <div className="supervisor-config-title">RF Test</div>
                            <SettingCheckBoxComponent
                                label={"Debug Mode"}
                                set={() => setRfTestDebugMode(p => !p)}
                                value={rfTestDebugMode}
                            />
                            <SettingCheckBoxComponent
                                label={"Is enabled?"}
                                set={() => setRfTestIsEnabled(p => !p)}
                                value={rfTestIsEnabled}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Worked time"}
                                value={rfTestWorkedTime}
                                setValue={setRfTestWorkedTime}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                    </div>
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={""}
                            label={"Default scenario delay milliseconds"}
                            value={defaultScenarioDelayMilliseconds}
                            setValue={setDefaultScenarioDelayMilliseconds}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingCheckBoxComponent
                            label={"Update whole and fraction after for glass devices"}
                            set={() => setUpdateWholeAndFraction(p => !p)}
                            value={updateWholeAndFraction}
                        />
                        <SettingCheckBoxComponent
                            label={"Is send local returns ack?"}
                            set={() => setIsSendLocalReturns(p => !p)}
                            value={isSendLocalReturns}
                        />
                        <SettingCheckBoxComponent
                            label={"Is docker?"}
                            set={() => setIsDocker(p => !p)}
                            value={isDocker}
                        />
                        <div className="supervisor-config-set-row inside-margin">
                            <div className="supervisor-config-title">Timezone</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Time offset in minutes"}
                                value={timeOffsetInMinutes}
                                setValue={setTimeOffsetInMinutes}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Timezone"}
                                value={timezone}
                                setValue={setTimezone}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingCheckBoxComponent
                                label={"Is manual timezone offset?"}
                                set={() => setIsManualTimezone(p => !p)}
                                value={isManualTimezone}
                            />
                        </div>
                        <div className="supervisor-config-set-row inside-margin">
                            <div className="supervisor-config-title">I Heart Beat</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Heart beat period in seconds"}
                                value={heartBeatPeriod}
                                setValue={setHeartBeatPeriod}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Heart beat url"}
                                value={heartBeatUrl}
                                setValue={setHeartBeatUrl}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Heart beat auth"}
                                value={heartBeatAuth}
                                setValue={setHeartBeatAuth}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="supervisor-config-set-row">
                            <div className="supervisor-config-title">Call Alarm</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Url"}
                                value={callAlarmUrl}
                                setValue={setCallAlarmUrl}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Token"}
                                value={callAlarmToken}
                                setValue={setCallAlarmToken}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Cookie"}
                                value={callAlarmCookie}
                                setValue={setCallAlarmCookie}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <SettingInputComponent
                            placeholder={""}
                            label={"Version"}
                            value={version}
                            setValue={setVersion}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <div className="supervisor-config-set-row inside-margin">
                            <div className="supervisor-config-title">Concierge Settings</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Concierge url port"}
                                value={conciergeUrlPort}
                                setValue={setConciergeUrlPort}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Concierge schema for message"}
                                value={conciergeSchemeForMessage}
                                setValue={setConciergeSchemeForMessage}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Concierge schema"}
                                value={conciergeScheme}
                                setValue={setConciergeScheme}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Concierge api websocket"}
                                value={conciergeApiWebsocket}
                                setValue={setConciergeApiWebsocket}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Concierge api message"}
                                value={conciergeApiMessage}
                                setValue={setConciergeApiMessage}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="supervisor-config-set-row inside-margin">
                            <div className="supervisor-config-title">Weather Settings</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Weather API key"}
                                value={weatherApiKey}
                                setValue={setWeatherApiKey}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Weather API url"}
                                value={weatherApiUrl}
                                setValue={setWeatherApiUrl}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Weather API value"}
                                value={weatherApiValue}
                                setValue={setWeatherApiValue}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                    </div>
                </div>
            </SettingsPageContainer>
            {renderKeyboard()}
        </PasswordContainer>
    )
})
